import * as t from 'io-ts';
import {isString} from 'lodash';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {randomString} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {RAsyncStatus} from '../../store/appTypes';
import {makeApiSerializableType, makeApiType, makeEnumRuntimeType} from './apiTypes';

/*
 * Runtime type.
 */

// Image file types supported by most browsers
// Note: svg is not included because specially crafted svg files can be a security risk causing Denial of Service
export const attachmentPreviewableImageTypes = [
  'image/apng',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/bmp',
  'image/x-icon',
];

export enum AttachmentStatusesEnum {
  UPLOADED,
  DRAFT,
  FILE,
}

export enum AttachmentVisualContentTypesEnum {
  ARCHIVE = 'ARCHIVE',
  AUDIO = 'AUDIO',
  CALENDAR = 'CALENDAR',
  CODE = 'CODE',
  EXCEL = 'EXCEL',
  GENERIC = 'GENERIC',
  IMAGE = 'IMAGE',
  PDF = 'PDF',
  POWERPOINT = 'POWERPOINT',
  TEXT = 'TEXT',
  VIDEO = 'VIDEO',
  WORD = 'WORD',
}

const RSimpleAttachmentDirty = t.readonly(
  t.type({
    name: t.string,
  }),
);

const RBaseAttachment = t.type({
  uid: t.string,
  linkId: t.union([t.Integer, t.undefined]),
  name: t.string,
  size: t.Integer,
  contentType: t.string,
  visualContentType: makeEnumRuntimeType(AttachmentVisualContentTypesEnum),
  cid: t.string,
  giphyId: t.union([t.string, t.undefined]),
  isInline: t.boolean,
  attachedAt: t.union([t.number, t.undefined]),
  hasThumbnail: t.union([t.boolean, t.undefined]),
});

const RUploadedAttachmentDirty = t.intersection([
  RBaseAttachment,
  t.type({
    status: t.literal(AttachmentStatusesEnum.UPLOADED),
    url: t.string,
  }),
]);

const RDraftAttachmentDirty = t.intersection([
  RBaseAttachment,
  t.type({
    status: t.literal(AttachmentStatusesEnum.DRAFT),
    hasFailed: t.boolean,
    progress: t.Integer,
  }),
]);

const RFileAttachmentDirty = t.intersection([
  RBaseAttachment,
  t.type({
    status: t.literal(AttachmentStatusesEnum.FILE),
    uploadStatus: RAsyncStatus,
  }),
]);

const RORUploadedAttachmentDirty = t.readonly(RUploadedAttachmentDirty);
const RODraftAttachmentDirty = t.readonly(RDraftAttachmentDirty);
const ROFileAttachmentDirty = t.readonly(RFileAttachmentDirty);

const RAttachmentDirty = t.readonly(
  t.taggedUnion('status', [RUploadedAttachmentDirty, RDraftAttachmentDirty]),
);

const RServerAttachment = t.readonly(
  t.type({
    uid: t.string,
    url: t.string,
    size: t.Integer,
  }),
);

/*
 * Type.
 */

export interface SimpleAttachmentModel extends t.TypeOf<typeof RSimpleAttachmentDirty> {}
export interface UploadedAttachmentModel extends t.TypeOf<typeof RUploadedAttachmentDirty> {}
export interface DraftAttachmentModel extends t.TypeOf<typeof RDraftAttachmentDirty> {}
export interface FileAttachmentModel extends t.TypeOf<typeof RFileAttachmentDirty> {}
export interface ServerAttachmentModel extends t.TypeOf<typeof RServerAttachment> {}

export type AttachmentModel = UploadedAttachmentModel | DraftAttachmentModel | FileAttachmentModel;

export const RSimpleAttachment = t.clean<SimpleAttachmentModel>(RSimpleAttachmentDirty as any);
export const RUploadedAttachment = t.clean<UploadedAttachmentModel>(RORUploadedAttachmentDirty as any);
export const RDraftAttachment = t.clean<DraftAttachmentModel>(RODraftAttachmentDirty as any);
export const RFileAttachment = t.clean<FileAttachmentModel>(ROFileAttachmentDirty as any);
export const RAttachment = t.clean<AttachmentModel>(RAttachmentDirty as any);
const RUploadedAttachments = t.readonlyArray(RUploadedAttachment);

/*
 * Type guard.
 */

export function isAttachmentModel(src: SimpleAttachmentModel | AttachmentModel): src is AttachmentModel {
  return Boolean((src as any).uid);
}

export function isUploadedAttachmentModel(src: any): src is UploadedAttachmentModel {
  return isString(src.uid) && src.status === AttachmentStatusesEnum.UPLOADED;
}

/*
 * IO methods.
 */

export function parseSimpleAttachment(src: any): SimpleAttachmentModel {
  return {
    name: src.name,
  };
}

export function parseUploadedAttachment(src: any): UploadedAttachmentModel {
  return {
    status: AttachmentStatusesEnum.UPLOADED,
    uid: src.uid,
    linkId: src.id,
    url: src.url,
    name: src.name,
    size: src.size,
    contentType: src.content_type,
    attachedAt: src.attached_at,
    visualContentType: parseVisualContentType(src.content_type && src.content_type.toLowerCase()),
    cid: src.cid,
    giphyId: src.giphy_id,
    isInline: src.inline,
    hasThumbnail: Boolean(src.thumbnail_width && src.thumbnail_height),
  };
}

export function parseFileAttachment(src: File): FileAttachmentModel {
  const encodedName = encodeURIComponent(src.name);
  return {
    status: AttachmentStatusesEnum.FILE,
    linkId: undefined,
    name: src.name,
    size: src.size,
    contentType: src.type,
    visualContentType: parseVisualContentType(src.type && src.type.toLowerCase()),
    giphyId: undefined,
    isInline: false,
    hasThumbnail: false,
    uploadStatus: AsyncStatusesEnum.LOADING,
    // Not used for anything
    attachedAt: undefined,
    uid: `uid-${src.lastModified}-${encodedName}`,
    cid: `cid-${src.lastModified}-${encodedName}`,
  };
}

function parseUploadedAttachments(src: any): ReadonlyArray<UploadedAttachmentModel> {
  return src.attachments && src.attachments.map(parseUploadedAttachment);
}

export function parseServerAttachment(src: any): ServerAttachmentModel {
  return {
    uid: src.uid,
    url: src.url,
    size: src.size,
  };
}

export function parseEmlAttachment(src: any): UploadedAttachmentModel {
  return {
    status: AttachmentStatusesEnum.UPLOADED,
    uid: randomString(),
    linkId: undefined,
    name: src.name,
    contentType: src.content_type,
    visualContentType: parseVisualContentType(src.content_type && src.content_type.toLowerCase()),
    url: src.url,
    size: src.size,
    cid: randomString(),
    giphyId: src.giphy_id,
    isInline: false,
    attachedAt: undefined,
    hasThumbnail: false,
  };
}

export function serializeAttachment(attachment: AttachmentModel): any {
  return {
    uid: attachment.uid,
    name: attachment.name,
    giphy_id: attachment.giphyId,
    inline: attachment.isInline,
    attached_at: attachment.attachedAt,
    cid: attachment.cid,
  };
}

export function parseVisualContentType(contentType: string): AttachmentVisualContentTypesEnum {
  if (contentType.startsWith('image/')) {
    return AttachmentVisualContentTypesEnum.IMAGE;
  }

  if (contentType.startsWith('audio/')) {
    return AttachmentVisualContentTypesEnum.AUDIO;
  }

  if (contentType.startsWith('video/')) {
    return AttachmentVisualContentTypesEnum.VIDEO;
  }

  switch (contentType) {
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.template':
      return AttachmentVisualContentTypesEnum.WORD;

    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.template':
    case 'text/csv':
      return AttachmentVisualContentTypesEnum.EXCEL;

    case 'application/vnd.ms-powerpoint':
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    case 'application/vnd.openxmlformats-officedocument.presentationml.template':
    case 'application/vnd.openxmlformats-officedocument.presentationml.slideshow':
      return AttachmentVisualContentTypesEnum.POWERPOINT;

    case 'application/pdf':
      return AttachmentVisualContentTypesEnum.PDF;

    case 'text/plain':
    case 'text/markdown':
      return AttachmentVisualContentTypesEnum.TEXT;

    case 'application/ics':
    case 'text/calendar':
      return AttachmentVisualContentTypesEnum.CALENDAR;

    case 'application/javascript':
    case 'application/json':
    case 'text/html':
    case 'text/css':
    case 'text/php':
    case 'text/xml':
      return AttachmentVisualContentTypesEnum.CODE;

    case 'application/zip':
    case 'application/x-tar':
    case 'application/x-gzip':
    case 'application/x-rar-compressed':
    case 'application/x-7z-compressed':
      return AttachmentVisualContentTypesEnum.ARCHIVE;

    default:
      return AttachmentVisualContentTypesEnum.GENERIC;
  }
}

/*
 * API type.
 */

export const ApiAttachment = makeApiSerializableType(
  'attachment',
  RAttachment,
  parseUploadedAttachment,
  serializeAttachment,
);

export const ApiUploadedAttachments = makeApiType(
  'attachments',
  RUploadedAttachments,
  parseUploadedAttachments,
);

export const ApiServerAttachment = makeApiType('serverAttachment', RServerAttachment, parseServerAttachment);
